import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useCart } from '../context/CartContext';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
}));

const SummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}));

const steps = ['Cart', 'Checkout', 'Payment'];

const Checkout = () => {
  const navigate = useNavigate();
  const {
    getCartItems,
    calculateSubtotal,
    calculateShipping,
    calculateTax,
    calculateTotal
  } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stripeLoaded, setStripeLoaded] = useState(false);

  // Detect environment
  const isProduction = !window.location.hostname.includes('localhost') && 
                      !window.location.hostname.includes('127.0.0.1');
  const API_URL = isProduction ? '' : 'http://localhost:5001';

  useEffect(() => {
    // Load Stripe.js when the component mounts
    const loadStripeScript = () => {
      if (!window.Stripe) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => {
          console.log('Stripe script loaded');
          setStripeLoaded(true);
        };
        document.head.appendChild(script);
      } else {
        setStripeLoaded(true);
      }
    };

    loadStripeScript();
  }, []);

  // Get cart items
  const cartItems = getCartItems();

  // Format currency
  const formatCurrency = (amount) => {
    return '£' + amount.toFixed(2);
  };

  // Handle checkout button click
  const handleCheckout = async () => {
    if (!stripeLoaded) {
      setError('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Process items to ensure they have proper image URLs
      const processedItems = cartItems.map(item => {
        const processedItem = { ...item };
        
        if (processedItem.image && !processedItem.image.startsWith('http')) {
          delete processedItem.image;
        }
        
        return processedItem;
      });

      // Create request data
      const requestData = { 
        items: processedItems,
        successUrl: `${window.location.origin}/order-confirmation`,
        cancelUrl: `${window.location.origin}/cart`  // Return to cart on cancel
      };
      
      // Use the configured API URL
      const checkoutUrl = `${API_URL}/api/checkout/create-checkout-session`;
      
      const response = await fetch(checkoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (data.url) {
        console.log('Redirecting to Stripe Checkout');
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server (missing checkout URL)');
      }
    } catch (err) {
      console.error('Error initializing checkout:', err);
      setError('Failed to initialize checkout: ' + (err.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // If cart is empty, redirect to cart page
  if (cartItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <IconButton onClick={() => navigate('/cart')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" display="inline">
            Checkout
          </Typography>
        </Box>
        
        <StyledPaper>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your cart is empty. Please add items to your cart before proceeding to checkout.
          </Alert>
          
          <Button 
            variant="contained" 
            onClick={() => navigate('/cart')}
            startIcon={<ShoppingCartIcon />}
          >
            Go to Cart
          </Button>
        </StyledPaper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <IconButton onClick={() => navigate('/cart')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" display="inline">
          Checkout
        </Typography>
      </Box>
      
      <Stepper activeStep={1} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Grid container spacing={4}>
        {/* Order Summary */}
        <Grid item xs={12} md={8}>
          <StyledPaper>
            <Typography variant="h5" gutterBottom>Order Summary</Typography>
            
            <Box sx={{ my: 3 }}>
              {cartItems.map((item) => (
                <Box key={item.id} sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
                  {item.image && (
                    <Box 
                      component="img" 
                      src={item.image.startsWith('http') ? item.image : '/qr-placeholder.png'} 
                      alt={item.name}
                      sx={{ width: 50, height: 50, mr: 2, borderRadius: 1 }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quantity: {item.quantity}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1">
                    {formatCurrency(item.price * item.quantity)}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mt: 3 }}>
              <SummaryItem>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">{formatCurrency(calculateSubtotal())}</Typography>
              </SummaryItem>
              
              <SummaryItem>
                <Typography variant="body1">Shipping</Typography>
                <Typography variant="body1">
                  {calculateShipping() === 0 
                    ? 'Free' 
                    : formatCurrency(calculateShipping())}
                </Typography>
              </SummaryItem>
              
              <SummaryItem>
                <Typography variant="body1">Tax</Typography>
                <Typography variant="body1">{formatCurrency(calculateTax())}</Typography>
              </SummaryItem>
              
              <Divider sx={{ my: 2 }} />
              
              <SummaryItem>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6">{formatCurrency(calculateTotal())}</Typography>
              </SummaryItem>
            </Box>
          </StyledPaper>
        </Grid>
        
        {/* Payment */}
        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Typography variant="h5" gutterBottom>Payment</Typography>
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                You'll be redirected to our secure payment processor to complete your purchase.
              </Alert>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                fullWidth
                disabled={loading || !stripeLoaded}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CreditCardIcon />}
                onClick={handleCheckout}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Processing...' : 'Pay Now'}
              </Button>
              
              <Button 
                variant="outlined"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                onClick={() => navigate('/cart')}
                sx={{ mt: 2 }}
              >
                Return to Cart
              </Button>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Checkout;